import React, { useState } from 'react';
import { siteContent } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FUTURE INTEGRATION: Online Bookings
 * 
 * To add a booking system (Calendly, Setmore, or similar):
 * 
 * Option 1 - Embed widget:
 *   Add an iframe or embed script below the contact form or as an alternative CTA.
 *   Example: <iframe src="https://calendly.com/siteandsignal/15min" />
 * 
 * Option 2 - Replace form with booking link:
 *   Change the primary CTA button to link directly to the booking page.
 *   Example: window.open('https://calendly.com/siteandsignal/15min', '_blank')
 * 
 * Option 3 - Add as secondary option:
 *   Keep the form but add "Or book a call directly" link below.
 * 
 * Recommended: Start with Option 3 to test demand before replacing the form.
 */

export const Contact = () => {
  const { contact } = siteContent;
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    businessType: '',
    website: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/enquiry`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        businessType: formData.businessType.trim() || null,
        message: null,
        website: formData.website
      });
      
      if (response.data.success) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', businessType: '', website: '' });
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.detail || 'Too many submissions. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white border-b border-stone-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
            {contact.title}
          </h2>
          
          {submitted ? (
            <div className="mt-8 bg-stone-50 border border-stone-200 rounded-lg p-6">
              <p className="text-lg font-medium text-stone-800">Thanks!</p>
              <p className="mt-2 text-stone-600 text-sm">We'll be in touch within 24 hours.</p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="mt-4 border-stone-300 text-stone-700 hover:bg-stone-100 text-sm"
              >
                Send another
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4 text-left">
              {/* Honeypot */}
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <Input name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleChange} />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              <div>
                <Label htmlFor="name" className="text-stone-700 text-sm">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 bg-white border-stone-300"
                  disabled={submitting}
                />
              </div>
              
              <div>
                <Label htmlFor="phone" className="text-stone-700 text-sm">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 bg-white border-stone-300"
                  disabled={submitting}
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-stone-700 text-sm">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 bg-white border-stone-300"
                  disabled={submitting}
                />
              </div>
              
              <div>
                <Label htmlFor="businessType" className="text-stone-700 text-sm">Type of business</Label>
                <Input
                  id="businessType"
                  name="businessType"
                  type="text"
                  placeholder="e.g. Plumber, Gardener"
                  value={formData.businessType}
                  onChange={handleChange}
                  className="mt-1 bg-white border-stone-300"
                  disabled={submitting}
                />
              </div>
              
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3 text-base font-medium h-auto"
                >
                  {submitting ? 'Sending...' : contact.cta}
                </Button>
                <p className="mt-2 text-xs text-stone-500 text-center">
                  {contact.microcopy}
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
